import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormHelperText from "@mui/material/FormHelperText";
import OutlinedInput from "@mui/material/OutlinedInput";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { toast } from "react-toastify";
import ConfirmDelete from "../../components/ConfirmDelete";
import api from "../../utils/api";

const languages = ["en", "hy", "ru"];
const languageLabels = { en: "English", hy: "Armenian", ru: "Russian" };

const getError = (errorsObj, field) => {
  const v = errorsObj[field];
  if (Array.isArray(v) && v.length) return v[0];
  if (typeof v === "string") return v;
  return "";
};

const defaultSubject = () => ({
  id: null,
  title_en: "",
  title_hy: "",
  title_ru: "",
  color: "#780000",
  background_color: "#ffffff",
  link: "",
  sort_order: 0,
});

export default function Subjects() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [errors, setErrors] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(defaultSubject());

  const loadSubjects = () => {
    return api.get("admin/content/subjects").then((res) => {
      const list = res.data?.data?.subjects ?? [];
      setSubjects(list);
    });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadSubjects()
      .catch(() => setSubjects([]))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const openAdd = () => {
    setEditing({ ...defaultSubject(), sort_order: subjects.length });
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (subject) => {
    setEditing({
      id: subject.id,
      title_en: subject.title_en ?? "",
      title_hy: subject.title_hy ?? "",
      title_ru: subject.title_ru ?? "",
      color: subject.color || "#780000",
      background_color: subject.background_color || "#ffffff",
      link: subject.link ?? "",
      sort_order: subject.sort_order ?? 0,
    });
    setErrors({});
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(defaultSubject());
    setErrors({});
  };

  const setField = (field, value) => {
    setEditing((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleColorInput = (field, fallback) => (e) => {
    let v = (e.target.value || "").trim();
    if (v && !v.startsWith("#")) v = "#" + v;
    if (/^#[0-9A-Fa-f]{6}$/.test(v) || v === "" || v === "#") setField(field, v || fallback);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setErrors({});
    const payload = {
      title_en: editing.title_en || "",
      title_hy: editing.title_hy || "",
      title_ru: editing.title_ru || "",
      color: editing.color || "#780000",
      background_color: editing.background_color || "#ffffff",
      link: (editing.link || "").trim(),
      sort_order: editing.sort_order ?? 0,
    };
    try {
      if (editing.id != null) {
        await api.put(`admin/content/subjects/${editing.id}`, payload);
        toast.success("Subject updated.");
      } else {
        await api.post("admin/content/subjects", payload);
        toast.success("Subject created.");
      }
      await loadSubjects();
      closeDialog();
    } catch (e) {
      const errs = e?.response?.data?.errors ?? e?.errors;
      if (errs && typeof errs === "object") setErrors(errs);
      const msg = e?.response?.data?.message || (errs ? "Please fix the validation errors." : "Failed to save subject.");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`admin/content/subjects/${id}`);
      toast.success("Subject deleted.");
      await loadSubjects();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to delete subject.");
    }
  };

  const displayTitle = (s) => s.title_en || s.title_hy || s.title_ru || `#${s.id}`;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 700, mx: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography component="h2" variant="h5" sx={{ my: 2, borderBottom: "1px solid", borderColor: "divider", pb: 1 }}>
        Home Page Subjects
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 1 }}>
        Subjects shown in the slider on the home page. Add titles in English, Armenian, and Russian and set a color.
      </Typography>

     

      <List dense sx={{ bgcolor: "background.paper", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
        {subjects.length === 0 ? (
          <ListItem>
            <ListItemText secondary="No subjects yet. Click «Add subject» to create one." />
          </ListItem>
        ) : (
          subjects.map((s) => (
            <ListItem
              key={s.id}
              secondaryAction={
                <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <IconButton size="small" onClick={() => openEdit(s)} aria-label="Edit">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <ConfirmDelete confirmText="Delete this subject?" dialogTitle="Delete subject" onConfirm={() => handleDelete(s.id)}>
                    <IconButton size="small" color="error" aria-label="Delete">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ConfirmDelete>
                </Box>
              }
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mr: 1.5, flexShrink: 0 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 24,
                    borderRadius: 1,
                    bgcolor: s.background_color || "#ffffff",
                    border: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: s.color || "#780000" }} />
                </Box>
              </Box>
              <ListItemText primary={displayTitle(s)} secondary={`Order: ${s.sort_order}`} />
            </ListItem>
          ))
        )}
      </List>

      <Box  sx={{display : "flex", justifyContent: "center" }}>       
        <Button startIcon={<AddIcon />} onClick={openAdd} variant="outlined" size="small" sx={{ alignSelf: "flex-start" }}>
          Add subject
        </Button>
      </Box> 

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing.id != null ? "Edit subject" : "Add subject"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {languages.map((lang) => (
              <FormControl key={lang} fullWidth error={!!getError(errors, `title_${lang}`)}>
                <FormLabel sx={{ display: "block", mb: 0.5 }} htmlFor={`subject-title-${lang}`}>
                  Title ({languageLabels[lang]})
                </FormLabel>
                <OutlinedInput
                  id={`subject-title-${lang}`}
                  fullWidth
                  size="small"
                  value={editing[`title_${lang}`] || ""}
                  onChange={(e) => setField(`title_${lang}`, e.target.value)}
                />
                {getError(errors, `title_${lang}`) && (
                  <FormHelperText>{getError(errors, `title_${lang}`)}</FormHelperText>
                )}
              </FormControl>
            ))}

            <FormControl fullWidth error={!!getError(errors, "color")}>
              <FormLabel sx={{ display: "block", mb: 1 }}>Text color</FormLabel>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <input
                  type="color"
                  value={editing.color || "#780000"}
                  onChange={(e) => setField("color", e.target.value)}
                  style={{ width: 48, height: 36, border: "1px solid #ccc", borderRadius: 4, cursor: "pointer", padding: 0 }}
                />
                <OutlinedInput
                  size="small"
                  value={editing.color || ""}
                  onChange={handleColorInput("color", "#780000")}
                  placeholder="#780000"
                  sx={{ flex: 1, minWidth: 0 }}
                />
              </Box>
              {getError(errors, "color") && <FormHelperText>{getError(errors, "color")}</FormHelperText>}
            </FormControl>

            <FormControl fullWidth error={!!getError(errors, "background_color")}>
              <FormLabel sx={{ display: "block", mb: 1 }}>Background color</FormLabel>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <input
                  type="color"
                  value={editing.background_color || "#ffffff"}
                  onChange={(e) => setField("background_color", e.target.value)}
                  style={{ width: 48, height: 36, border: "1px solid #ccc", borderRadius: 4, cursor: "pointer", padding: 0 }}
                />
                <OutlinedInput
                  size="small"
                  value={editing.background_color || ""}
                  onChange={handleColorInput("background_color", "#ffffff")}
                  placeholder="#ffffff"
                  sx={{ flex: 1, minWidth: 0 }}
                />
              </Box>
              {getError(errors, "background_color") && <FormHelperText>{getError(errors, "background_color")}</FormHelperText>}
            </FormControl>

            <FormControl fullWidth error={!!getError(errors, "link")}>
              <FormLabel sx={{ display: "block", mb: 0.5 }} htmlFor="subject-link">
                Link (optional)
              </FormLabel>
              <OutlinedInput
                id="subject-link"
                fullWidth
                size="small"
                value={editing.link || ""}
                onChange={(e) => setField("link", e.target.value)}
                placeholder="https://example.com or /courses/english"
              />
              <FormHelperText>
                {getError(errors, "link") || "If set, the subject on the home page becomes clickable and opens this URL."}
              </FormHelperText>
            </FormControl>

            <FormControl fullWidth>
              <FormLabel sx={{ display: "block", mb: 0.5 }} htmlFor="subject-sort_order">
                Sort order
              </FormLabel>
              <OutlinedInput
                id="subject-sort_order"
                type="number"
                inputProps={{ min: 0 }}
                size="small"
                value={editing.sort_order ?? 0}
                onChange={(e) => setField("sort_order", parseInt(e.target.value, 10) || 0)}
              />
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? <CircularProgress size={24} /> : editing.id != null ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
